import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import MobileHeader from '../components/MobileHeader';
import BackButton from '../components/BackButton';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  User, Lock, Bell, Palette, Shield, Key, Globe, 
  Save, Eye, EyeOff, Monitor, FileDown, Trash2, 
  AlertTriangle, Copy, Check, RefreshCw, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  
  // Profile
  const [profileData, setProfileData] = useState({ name: '', email: '', phone: '', bio: '', country: '' });
  // Security
  const [securityData, setSecurityData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [sessions, setSessions] = useState([]);
  // Notifications
  const [notifications, setNotifications] = useState({ emailAlerts: true, tradeAlerts: true, priceAlerts: false, newsletter: true, smsAlerts: false });
  // Appearance
  const [appearance, setAppearance] = useState({ theme: 'dark', language: 'en', currency: 'USD', timezone: 'UTC' });
  // Privacy
  const [privacy, setPrivacy] = useState({ showProfile: true, showBalance: false, showActivity: true });
  // API Keys
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [copiedKey, setCopiedKey] = useState(false);
  // Delete
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => { loadAllSettings(); }, []);

  const loadAllSettings = async () => {
    try {
      const [profileRes, notifRes, appearRes, privRes, keysRes, sessRes] = await Promise.all([
        api.get('/settings/profile').catch(() => ({ data: {} })),
        api.get('/settings/notifications').catch(() => ({ data: {} })),
        api.get('/settings/appearance').catch(() => ({ data: {} })),
        api.get('/settings/privacy').catch(() => ({ data: {} })),
        api.get('/settings/api-keys').catch(() => ({ data: { api_keys: [] } })),
        api.get('/settings/sessions').catch(() => ({ data: { sessions: [] } })),
      ]);
      setProfileData({ name: profileRes.data.name || '', email: profileRes.data.email || '', phone: profileRes.data.phone || '', bio: profileRes.data.bio || '', country: profileRes.data.country || '' });
      if (notifRes.data.emailAlerts !== undefined) setNotifications(notifRes.data);
      if (appearRes.data.theme) setAppearance(appearRes.data);
      if (privRes.data.showProfile !== undefined) setPrivacy(privRes.data);
      setApiKeys(keysRes.data.api_keys || []);
      setSessions(sessRes.data.sessions || []);
    } catch (e) { console.error('Settings load error:', e); }
  };

  // ---- Save Handlers ----
  const handleSaveProfile = async () => {
    if (!profileData.name.trim()) { toast.error('Name is required'); return; }
    if (profileData.phone && !/^[\d+\-\s()]{7,20}$/.test(profileData.phone)) { toast.error('Invalid phone number format'); return; }
    try {
      setLoading(true);
      await api.put('/settings/profile', { name: profileData.name.trim(), phone: profileData.phone.trim(), bio: profileData.bio.trim(), country: profileData.country });
      toast.success('Profile updated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save profile'); }
    finally { setLoading(false); }
  };

  const handleChangePassword = async () => {
    if (!securityData.currentPassword) { toast.error('Enter current password'); return; }
    if (securityData.newPassword.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(securityData.newPassword) || !/[0-9]/.test(securityData.newPassword)) { toast.error('Password needs uppercase letter and number'); return; }
    if (securityData.newPassword !== securityData.confirmPassword) { toast.error('Passwords do not match'); return; }
    try {
      setLoading(true);
      await api.post('/settings/password', { current_password: securityData.currentPassword, new_password: securityData.newPassword });
      toast.success('Password changed!');
      setSecurityData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change password'); }
    finally { setLoading(false); }
  };

  const handleSaveNotifications = async () => {
    try { setLoading(true); await api.put('/settings/notifications', notifications); toast.success('Notification preferences saved!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const handleSaveAppearance = async () => {
    try { setLoading(true); await api.put('/settings/appearance', appearance); toast.success('Appearance saved!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const handleSavePrivacy = async () => {
    try { setLoading(true); await api.put('/settings/privacy', privacy); toast.success('Privacy settings saved!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const handleGenerateAPIKey = async () => {
    const name = newKeyName.trim() || `API Key ${apiKeys.length + 1}`;
    try {
      setLoading(true);
      const res = await api.post('/settings/api-keys', { name });
      if (res.data.success) {
        setNewKeyValue(res.data.api_key.key);
        setApiKeys([...apiKeys, { id: res.data.api_key.id, name: res.data.api_key.name, key: res.data.api_key.key, created: res.data.api_key.created?.split('T')[0], status: 'active' }]);
        setNewKeyName('');
        toast.success('API key generated! Copy it now.');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate key'); }
    finally { setLoading(false); }
  };

  const handleRevokeAPIKey = async (id) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    try { setLoading(true); await api.delete(`/settings/api-keys/${id}`); setApiKeys(apiKeys.filter(k => k.id !== id)); toast.success('Key revoked'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to revoke'); }
    finally { setLoading(false); }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/settings/export-data');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `phantom-trades-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Data exported!');
    } catch { toast.error('Export failed'); }
    finally { setLoading(false); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    if (!deletePassword) { toast.error('Enter your password'); return; }
    try {
      setLoading(true);
      await api.post('/settings/delete-account', { password: deletePassword, confirmation: deleteConfirm });
      toast.success('Account deleted.');
      logout(); navigate('/');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
    finally { setLoading(false); }
  };

  const getTimeAgo = (date) => {
    const s = Math.floor((new Date() - new Date(date)) / 1000);
    if (s < 60) return 'Just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const getPasswordStrength = (pw) => {
    if (!pw) return { label: '', color: '', width: '0%' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { label: 'Weak', color: 'bg-red-500', width: '33%' };
    if (score <= 3) return { label: 'Medium', color: 'bg-yellow-500', width: '66%' };
    return { label: 'Strong', color: 'bg-green-500', width: '100%' };
  };

  const pwStrength = getPasswordStrength(securityData.newPassword);

  const tabs = [
    { id: 'profile', icon: User, label: 'Profile' },
    { id: 'security', icon: Lock, label: 'Security' },
    { id: 'notifications', icon: Bell, label: 'Notifications' },
    { id: 'appearance', icon: Palette, label: 'Appearance' },
    { id: 'privacy', icon: Shield, label: 'Privacy' },
    { id: 'apikeys', icon: Key, label: 'API Keys' },
  ];

  const Toggle = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-800/50 last:border-0">
      <div>
        <p className="text-white text-sm font-medium">{label}</p>
        {description && <p className="text-gray-500 text-xs mt-0.5">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-purple-600' : 'bg-gray-700'}`}
        data-testid={`toggle-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navbar isDashboard={true} />
      <MobileHeader isDashboard={true} />

      <div className="pt-20 sm:pt-24 pb-20 sm:pb-12 px-3 sm:px-4 md:px-6">
        <div className="container mx-auto max-w-5xl">
          <BackButton label="Back" />
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Settings</h1>
            <p className="text-gray-400 text-sm">Manage your account, security, and preferences</p>
          </div>

          {/* Tabs Navigation */}
          <div className="flex gap-1.5 mb-6 overflow-x-auto pb-2 scrollbar-hide">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    activeTab === tab.id ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/20' : 'bg-gray-900/50 text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="bg-gray-900/40 border border-gray-800/50 rounded-2xl p-5 sm:p-7 backdrop-blur-sm">

            {/* ===== PROFILE ===== */}
            {activeTab === 'profile' && (
              <div className="space-y-5" data-testid="settings-profile">
                <h2 className="text-xl font-bold flex items-center gap-2"><User className="h-5 w-5 text-purple-400" /> Profile Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Full Name *</Label>
                    <Input value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} className="mt-1 bg-gray-900/60 border-gray-700" placeholder="Your full name" data-testid="input-name" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Email</Label>
                    <Input value={profileData.email} disabled className="mt-1 bg-gray-900/60 border-gray-700 opacity-60 cursor-not-allowed" data-testid="input-email" />
                    <p className="text-gray-500 text-xs mt-1">Email cannot be changed</p>
                  </div>
                  <div>
                    <Label className="text-gray-300">Phone Number</Label>
                    <Input value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} className="mt-1 bg-gray-900/60 border-gray-700" placeholder="+1 (555) 000-0000" data-testid="input-phone" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Country</Label>
                    <select value={profileData.country} onChange={(e) => setProfileData({...profileData, country: e.target.value})} className="w-full mt-1 p-2.5 bg-gray-900/60 border border-gray-700 rounded-lg text-white text-sm" data-testid="select-country">
                      <option value="">Select Country</option>
                      {['US','GB','CA','AU','DE','FR','JP','KR','SG','AE','NG','ZA','GH','KE','IN','BR','MX','PH','VN','TH','ID','TR','NL','CH','SE','NO','PL','IT','ES','PT'].map(c => <option key={c} value={c}>{c}</option>)}
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Bio</Label>
                  <textarea value={profileData.bio} onChange={(e) => setProfileData({...profileData, bio: e.target.value})} rows={3} maxLength={200} className="w-full mt-1 p-2.5 bg-gray-900/60 border border-gray-700 rounded-lg text-white text-sm resize-none" placeholder="Tell us about yourself..." data-testid="input-bio" />
                  <p className="text-gray-500 text-xs mt-1">{profileData.bio.length}/200</p>
                </div>
                <Button onClick={handleSaveProfile} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="save-profile-btn">
                  <Save className="h-4 w-4 mr-2" />{loading ? 'Saving...' : 'Save Profile'}
                </Button>
              </div>
            )}

            {/* ===== SECURITY ===== */}
            {activeTab === 'security' && (
              <div className="space-y-6" data-testid="settings-security">
                <h2 className="text-xl font-bold flex items-center gap-2"><Lock className="h-5 w-5 text-purple-400" /> Security Settings</h2>

                {/* Change Password */}
                <div className="p-5 rounded-xl bg-gray-800/30 border border-gray-700/50 space-y-4">
                  <h3 className="font-semibold text-white">Change Password</h3>
                  <div>
                    <Label className="text-gray-300">Current Password</Label>
                    <div className="relative mt-1">
                      <Input type={showCurrentPw ? 'text' : 'password'} value={securityData.currentPassword} onChange={(e) => setSecurityData({...securityData, currentPassword: e.target.value})} className="bg-gray-900/60 border-gray-700 pr-10" data-testid="input-current-pw" />
                      <button onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-2.5 text-gray-400 hover:text-white">{showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-300">New Password</Label>
                    <div className="relative mt-1">
                      <Input type={showNewPw ? 'text' : 'password'} value={securityData.newPassword} onChange={(e) => setSecurityData({...securityData, newPassword: e.target.value})} className="bg-gray-900/60 border-gray-700 pr-10" placeholder="Min 8 chars, uppercase + number" data-testid="input-new-pw" />
                      <button onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-2.5 text-gray-400 hover:text-white">{showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                    {securityData.newPassword && (
                      <div className="mt-2">
                        <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div className={`h-full ${pwStrength.color} transition-all duration-300`} style={{ width: pwStrength.width }} />
                        </div>
                        <p className={`text-xs mt-1 ${pwStrength.color === 'bg-red-500' ? 'text-red-400' : pwStrength.color === 'bg-yellow-500' ? 'text-yellow-400' : 'text-green-400'}`}>{pwStrength.label}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-300">Confirm New Password</Label>
                    <Input type="password" value={securityData.confirmPassword} onChange={(e) => setSecurityData({...securityData, confirmPassword: e.target.value})} className="mt-1 bg-gray-900/60 border-gray-700" data-testid="input-confirm-pw" />
                    {securityData.confirmPassword && securityData.newPassword !== securityData.confirmPassword && (
                      <p className="text-red-400 text-xs mt-1">Passwords don't match</p>
                    )}
                  </div>
                  <Button onClick={handleChangePassword} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="change-pw-btn">
                    <Lock className="h-4 w-4 mr-2" />{loading ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>

                {/* Login Sessions */}
                <div className="p-5 rounded-xl bg-gray-800/30 border border-gray-700/50">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-purple-400" /> Recent Login Activity
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {sessions.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">No login sessions recorded</p>
                    ) : sessions.slice(0, 10).map((s, i) => {
                      const ua = s.user_agent || '';
                      let device = 'Unknown';
                      if (ua.includes('Chrome')) device = 'Chrome';
                      else if (ua.includes('Firefox')) device = 'Firefox';
                      else if (ua.includes('Safari')) device = 'Safari';
                      if (ua.includes('Windows')) device += ' / Windows';
                      else if (ua.includes('Mac')) device += ' / Mac';
                      else if (ua.includes('Linux')) device += ' / Linux';
                      else if (ua.includes('Android')) device += ' / Android';
                      else if (ua.includes('iPhone')) device += ' / iOS';
                      return (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg" data-testid={`session-${i}`}>
                          <div>
                            <p className="text-white text-sm font-medium">{device}</p>
                            <p className="text-gray-500 text-xs">IP: {s.ip_address || 'N/A'} &bull; {s.logged_in_at ? getTimeAgo(s.logged_in_at) : ''}</p>
                          </div>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${i === 0 ? 'bg-purple-600/20 text-purple-300' : 'bg-gray-800 text-gray-400'}`}>
                            {i === 0 ? 'Current' : 'Past'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ===== NOTIFICATIONS ===== */}
            {activeTab === 'notifications' && (
              <div className="space-y-5" data-testid="settings-notifications">
                <h2 className="text-xl font-bold flex items-center gap-2"><Bell className="h-5 w-5 text-purple-400" /> Notification Preferences</h2>
                <div className="p-5 rounded-xl bg-gray-800/30 border border-gray-700/50">
                  <Toggle checked={notifications.emailAlerts} onChange={(v) => setNotifications({...notifications, emailAlerts: v})} label="Email Alerts" description="Receive email for deposits, withdrawals, and important account events" />
                  <Toggle checked={notifications.tradeAlerts} onChange={(v) => setNotifications({...notifications, tradeAlerts: v})} label="Trade Alerts" description="Get notified when your trades are settled (win/loss)" />
                  <Toggle checked={notifications.priceAlerts} onChange={(v) => setNotifications({...notifications, priceAlerts: v})} label="Price Alerts" description="Receive alerts when crypto prices move significantly" />
                  <Toggle checked={notifications.newsletter} onChange={(v) => setNotifications({...notifications, newsletter: v})} label="Newsletter" description="Weekly market analysis and platform updates" />
                  <Toggle checked={notifications.smsAlerts} onChange={(v) => setNotifications({...notifications, smsAlerts: v})} label="SMS Alerts" description="Critical security alerts via SMS (requires phone number)" />
                </div>
                <Button onClick={handleSaveNotifications} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="save-notifications-btn">
                  <Save className="h-4 w-4 mr-2" />{loading ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            )}

            {/* ===== APPEARANCE ===== */}
            {activeTab === 'appearance' && (
              <div className="space-y-5" data-testid="settings-appearance">
                <h2 className="text-xl font-bold flex items-center gap-2"><Palette className="h-5 w-5 text-purple-400" /> Appearance & Display</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Theme</Label>
                    <select value={appearance.theme} onChange={(e) => setAppearance({...appearance, theme: e.target.value})} className="w-full mt-1 p-2.5 bg-gray-900/60 border border-gray-700 rounded-lg text-white text-sm" data-testid="select-theme">
                      <option value="dark">Dark Mode</option>
                      <option value="light">Light Mode (Coming Soon)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Language</Label>
                    <select value={appearance.language} onChange={(e) => setAppearance({...appearance, language: e.target.value})} className="w-full mt-1 p-2.5 bg-gray-900/60 border border-gray-700 rounded-lg text-white text-sm" data-testid="select-language">
                      <option value="en">English</option>
                      <option value="fr">French</option>
                      <option value="es">Spanish</option>
                      <option value="de">German</option>
                      <option value="ar">Arabic</option>
                      <option value="zh">Chinese</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Display Currency</Label>
                    <select value={appearance.currency} onChange={(e) => setAppearance({...appearance, currency: e.target.value})} className="w-full mt-1 p-2.5 bg-gray-900/60 border border-gray-700 rounded-lg text-white text-sm" data-testid="select-currency">
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (&euro;)</option>
                      <option value="GBP">GBP (&pound;)</option>
                      <option value="NGN">NGN (&#x20A6;)</option>
                      <option value="JPY">JPY (&yen;)</option>
                      <option value="BTC">BTC</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Timezone</Label>
                    <select value={appearance.timezone} onChange={(e) => setAppearance({...appearance, timezone: e.target.value})} className="w-full mt-1 p-2.5 bg-gray-900/60 border border-gray-700 rounded-lg text-white text-sm" data-testid="select-timezone">
                      <option value="UTC">UTC (GMT+0)</option>
                      <option value="America/New_York">Eastern (GMT-5)</option>
                      <option value="America/Chicago">Central (GMT-6)</option>
                      <option value="America/Los_Angeles">Pacific (GMT-8)</option>
                      <option value="Europe/London">London (GMT+0)</option>
                      <option value="Europe/Paris">Paris (GMT+1)</option>
                      <option value="Asia/Tokyo">Tokyo (GMT+9)</option>
                      <option value="Asia/Singapore">Singapore (GMT+8)</option>
                      <option value="Africa/Lagos">Lagos (GMT+1)</option>
                      <option value="Africa/Nairobi">Nairobi (GMT+3)</option>
                    </select>
                  </div>
                </div>
                <Button onClick={handleSaveAppearance} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="save-appearance-btn">
                  <Save className="h-4 w-4 mr-2" />{loading ? 'Saving...' : 'Save Appearance'}
                </Button>
              </div>
            )}

            {/* ===== PRIVACY ===== */}
            {activeTab === 'privacy' && (
              <div className="space-y-5" data-testid="settings-privacy">
                <h2 className="text-xl font-bold flex items-center gap-2"><Shield className="h-5 w-5 text-purple-400" /> Privacy & Data</h2>
                <div className="p-5 rounded-xl bg-gray-800/30 border border-gray-700/50">
                  <Toggle checked={privacy.showProfile} onChange={(v) => setPrivacy({...privacy, showProfile: v})} label="Public Profile" description="Allow other users to see your profile name and bio" />
                  <Toggle checked={privacy.showBalance} onChange={(v) => setPrivacy({...privacy, showBalance: v})} label="Show Balance on Dashboard" description="Display your total balance prominently (can hide for privacy)" />
                  <Toggle checked={privacy.showActivity} onChange={(v) => setPrivacy({...privacy, showActivity: v})} label="Show Activity Status" description="Let others know when you're online" />
                </div>
                <Button onClick={handleSavePrivacy} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="save-privacy-btn">
                  <Save className="h-4 w-4 mr-2" />{loading ? 'Saving...' : 'Save Privacy'}
                </Button>

                {/* Export & Delete */}
                <div className="mt-6 p-5 rounded-xl bg-blue-900/10 border border-blue-500/20">
                  <h3 className="font-semibold text-blue-300 flex items-center gap-2 mb-2"><FileDown className="h-4 w-4" /> Export Account Data</h3>
                  <p className="text-gray-400 text-sm mb-3">Download all your data (profile, wallet, trades, transactions) as JSON.</p>
                  <Button onClick={handleExportData} disabled={loading} variant="outline" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10" data-testid="export-data-btn">
                    <FileDown className="h-4 w-4 mr-2" />{loading ? 'Exporting...' : 'Export My Data'}
                  </Button>
                </div>

                <div className="mt-4 p-5 rounded-xl bg-red-900/10 border border-red-500/20">
                  <h3 className="font-semibold text-red-300 flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4" /> Danger Zone</h3>
                  <p className="text-gray-400 text-sm mb-3">Permanently delete your account and all data. This cannot be undone.</p>
                  {!showDeleteConfirm ? (
                    <Button onClick={() => setShowDeleteConfirm(true)} className="bg-red-600 hover:bg-red-700" data-testid="delete-account-btn">
                      <Trash2 className="h-4 w-4 mr-2" /> Delete Account
                    </Button>
                  ) : (
                    <div className="space-y-3 p-4 bg-red-950/30 rounded-lg border border-red-500/30">
                      <div>
                        <Label className="text-red-300 text-sm">Password</Label>
                        <Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} className="mt-1 bg-gray-900/60 border-red-500/30" placeholder="Your password" data-testid="delete-pw" />
                      </div>
                      <div>
                        <Label className="text-red-300 text-sm">Type DELETE to confirm</Label>
                        <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} className="mt-1 bg-gray-900/60 border-red-500/30" placeholder="DELETE" data-testid="delete-confirm" />
                      </div>
                      <div className="flex gap-3">
                        <Button onClick={handleDeleteAccount} disabled={loading || deleteConfirm !== 'DELETE'} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
                          {loading ? 'Deleting...' : 'Permanently Delete'}
                        </Button>
                        <Button onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteConfirm(''); }} variant="ghost" className="text-gray-400">Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== API KEYS ===== */}
            {activeTab === 'apikeys' && (
              <div className="space-y-5" data-testid="settings-apikeys">
                <h2 className="text-xl font-bold flex items-center gap-2"><Key className="h-5 w-5 text-purple-400" /> API Keys</h2>
                <p className="text-gray-400 text-sm">Manage API keys for programmatic access to your account. Keys are shown only once on creation.</p>

                {/* Generate new key */}
                <div className="p-5 rounded-xl bg-gray-800/30 border border-gray-700/50">
                  <h3 className="font-semibold text-white mb-3">Generate New Key</h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="Key name (e.g. Trading Bot)" className="flex-1 bg-gray-900/60 border-gray-700" data-testid="input-key-name" />
                    <Button onClick={handleGenerateAPIKey} disabled={loading} className="bg-purple-600 hover:bg-purple-700 whitespace-nowrap" data-testid="generate-key-btn">
                      <Key className="h-4 w-4 mr-2" />{loading ? 'Generating...' : 'Generate Key'}
                    </Button>
                  </div>

                  {newKeyValue && (
                    <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <p className="text-green-300 text-xs font-medium mb-2">New key created! Copy it now — it won't be shown again.</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs bg-gray-900 px-3 py-2 rounded text-green-200 font-mono break-all">{newKeyValue}</code>
                        <button onClick={() => handleCopyKey(newKeyValue)} className="p-2 bg-gray-800 rounded hover:bg-gray-700 transition-colors" data-testid="copy-key-btn">
                          {copiedKey ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4 text-gray-400" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Existing keys */}
                <div className="space-y-3">
                  {apiKeys.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Key className="h-10 w-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No API keys yet</p>
                    </div>
                  ) : apiKeys.map(key => (
                    <div key={key.id} className="flex items-center justify-between p-4 bg-gray-800/30 border border-gray-700/50 rounded-xl" data-testid={`api-key-${key.id}`}>
                      <div>
                        <p className="text-white font-medium text-sm">{key.name}</p>
                        <p className="text-gray-500 text-xs font-mono mt-0.5">{key.key}</p>
                        <p className="text-gray-600 text-xs mt-0.5">Created: {key.created}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${key.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {key.status}
                        </span>
                        <button onClick={() => handleRevokeAPIKey(key.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded transition-colors" title="Revoke">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
